import { createContext, useContext } from "react";
import { type SecurityTriggerStatus } from "@jsq/payments-components";

import { type LabelDisplaySetting } from "common/hooks/useLabelSettings/types";
import { type DateFormatType, type TimeFormatType } from "./types";

export type User = {
  id: string;
  fullName: string;
  emailAddress: string;
  dateFormat?: DateFormatType;
  timeFormat?: TimeFormatType;
  // Tri-state LP session-security verdict (TRUE / FALSE / UNAVAILABLE); server-gated
  // per FF-7744 (flag `lpSessionSecurityTriggers`).
  hasActiveSecurityTriggers: SecurityTriggerStatus;
};

export type PaymentPermission = {
  requirePositionPaymentPrefStaffApprovals: boolean;
  requirePositionPaymentPrefInvestorApprovals: boolean;
  requiredApprovalCount: number;
  requireDifferentApprover: boolean;
  requireNoteOnStaffPaymentInstructionChange: boolean;
  arenaId: string;
};

export type ManagerArena = {
  id: string;
  name: string;
  secondLevelDomain: string;
  logoUrl?: string;
  logoBackgroundColor?: string;
  supportEmail: string;
  perfMetricsAsOfDate: string | null;
  ownershipDisplayPrecision?: number;
  allowPortalInvestorReport: boolean;
  showDisregardedEntityQuestionnaire?: boolean | null;
  paymentPermission?: PaymentPermission | null;
  labelDisplaySettings?: LabelDisplaySetting[] | null;
};

export type ArenaContent = {
  name: string;
  arenaDomain: string;
  headerLogoUrl?: string;
  headerLogoUrl2x?: string;
  contactEmail?: string;
  contactPhone?: string;
};

export type AppContextData = {
  // user is optional, will be undefined in unauthenticated state
  user?: User;
  application: "singleManagerPortal" | "multiManagerPortal" | null;
  // managerArenas will be an empty array in unauthenticated state
  managerArenas: ManagerArena[];
  currentArenaContent?: ArenaContent;
  onlyOneGp: boolean;
  getLpPortalSettingsPath: () => string;
  getLpPortalSettingsAccountDetailsPath: () => string;
};

export const AppContext = createContext<AppContextData>({
  managerArenas: [],
  application: null,
  onlyOneGp: false,
  getLpPortalSettingsPath: () => "/lp/settings",
  getLpPortalSettingsAccountDetailsPath: () => "lp/settings/accounts",
});

export const useAppContext = () => useContext<AppContextData>(AppContext);
